/**
 * ShareBar — Bloco 2 (Fase A)
 *
 * 4 share actions for a candidate page:
 *   1. WhatsApp (primary, green) — pre-composed text with wa.me link
 *   2. Baixar card (Instagram/Feed) — downloads the 1080x1080 PNG
 *   3. Twitter/X — opens tweet composer
 *   4. Copiar link — copies canonical URL to clipboard
 *
 * The `share` URL points to the backend HTML endpoint that emits OG meta
 * tags, so link previews on WhatsApp/Twitter render the server-side card.
 */
import React, { useState } from "react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const fireShareEvent = (slug, canal) => {
  // Fire-and-forget analytics — never blocks share flow.
  try {
    fetch(`${BACKEND}/api/public/analytics/share`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ candidato_slug: slug, canal }),
      keepalive: true,
    }).catch(() => {});
  } catch { /* silent */ }
};


const buildWhatsAppText = (data) => {
  const partySigla = (data.current_party || "").match(/\(([^)]+)\)/)?.[1] || data.current_party || "";
  return (
`🔍 *Tô de Olho* pesquisou sobre
*${data.name}*
${[partySigla, data.position, data.state].filter(Boolean).join(" · ")}

O que encontramos: ${data.verdict?.titulo || "análise indisponível"}
Nota de integridade: ${data.integrity_score}/10

Fonte: TSE, TCU e Portal da Transparência
👉 ${BACKEND}/api/share/candidato/${data.slug}

_Grátis, simples e com fonte oficial._`
  );
};

const buildTwitterText = (data) => {
  const partySigla = (data.current_party || "").match(/\(([^)]+)\)/)?.[1] || "";
  const verdict = data.verdict?.titulo || "análise indisponível";
  return (
`Pesquisei ${data.name}${partySigla ? " (" + partySigla + ")" : ""} no Tô de Olho
${verdict} | Nota ${data.integrity_score}/10
Fonte: dados oficiais
${BACKEND}/api/share/candidato/${data.slug}
#TôDeOlho #Eleições2026 #Transparência`
  );
};

export function ShareBar({ candidate }) {
  const [copied, setCopied] = useState(false);
  const shareUrl = `${BACKEND}/api/share/candidato/${candidate.slug}`;
  const cardUrl = `${BACKEND}/api/public/candidatos/${candidate.slug}/card.png`;

  const handleWhatsApp = () => {
    fireShareEvent(candidate.slug, "whatsapp");
    const text = encodeURIComponent(buildWhatsAppText(candidate));
    window.open(`https://wa.me/?text=${text}`, "_blank", "noopener");
  };

  const handleTwitter = () => {
    fireShareEvent(candidate.slug, "twitter");
    const text = encodeURIComponent(buildTwitterText(candidate));
    window.open(`https://twitter.com/intent/tweet?text=${text}`, "_blank", "noopener");
  };

  const handleDownload = async () => {
    fireShareEvent(candidate.slug, "download");
    try {
      const res = await fetch(cardUrl);
      if (!res.ok) throw new Error("Falha ao baixar card");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `todeolho-${candidate.slug}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Card baixado! Pronto para postar no Instagram.");
    } catch (e) {
      toast.error("Não foi possível baixar o card agora.");
    }
  };

  const handleCopy = async () => {
    fireShareEvent(candidate.slug, "copy");
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copiado!");
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      // Fallback for older browsers / non-secure contexts
      const ta = document.createElement("textarea");
      ta.value = shareUrl;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); toast.success("Link copiado!"); }
      catch { toast.error("Não foi possível copiar."); }
      ta.remove();
    }
  };

  return (
    <div className="w-full flex flex-col gap-3" data-testid="share-bar">
      <button
        onClick={handleWhatsApp}
        data-testid="share-whatsapp-btn"
        className="w-full flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1DA851] text-white font-semibold py-3 px-5 rounded-lg transition-colors min-h-[44px]"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
        </svg>
        Compartilhar no WhatsApp
      </button>

      <div className="grid grid-cols-3 gap-3">
        <button
          onClick={handleDownload}
          data-testid="share-download-btn"
          className="flex flex-col items-center justify-center gap-1 bg-[#1E3A5F] hover:bg-[#28497a] text-white py-3 px-3 rounded-lg transition-colors min-h-[44px]"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
            <polyline points="7 10 12 15 17 10"/>
            <line x1="12" y1="15" x2="12" y2="3"/>
          </svg>
          <span className="text-xs font-medium">Baixar card</span>
        </button>

        <button
          onClick={handleTwitter}
          data-testid="share-twitter-btn"
          className="flex flex-col items-center justify-center gap-1 bg-[#111] hover:bg-[#333] text-white py-3 px-3 rounded-lg transition-colors min-h-[44px]"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
          </svg>
          <span className="text-xs font-medium">X / Twitter</span>
        </button>

        <button
          onClick={handleCopy}
          data-testid="share-copy-btn"
          className={`flex flex-col items-center justify-center gap-1 py-3 px-3 rounded-lg transition-colors min-h-[44px] ${copied ? "bg-[#006030] text-white" : "bg-white border border-slate-300 hover:border-slate-500 text-slate-800"}`}
        >
          {copied ? (
            <>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
              <span className="text-xs font-medium">Copiado!</span>
            </>
          ) : (
            <>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
              </svg>
              <span className="text-xs font-medium">Copiar link</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
}

export default ShareBar;
